import React, { Fragment, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaSpinner, FaBox, FaPen, FaTrash, FaChevronLeft, FaChevronRight, FaStar, FaRegStar, FaStarHalfAlt } from "react-icons/fa"; // added chevrons & stars for rating
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function ProductsPage() {
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [productData, setProductData] = useState({});
    const [productList, setProductList] = useState([]);
    const [uploadInProgress, setUploadInProgress] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [imageUploads, setImageUploads] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false
    });
    // Infinite Scroll state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sortBy, setSortBy] = useState('newest');

    // Bulk stock modal states
    const [bulkSegment, setBulkSegment] = useState('all');
    const [bulkSegmentValue, setBulkSegmentValue] = useState('');
    const [bulkAction, setBulkAction] = useState('set');
    const [bulkQuantity, setBulkQuantity] = useState('');
    const [bulkLoading, setBulkLoading] = useState(false);

    const handleBulkStockSubmit = async (e) => {
        e.preventDefault();
        if (!bulkQuantity || isNaN(Number(bulkQuantity)) || Number(bulkQuantity) < 0) {
            alert('Please enter a valid non-negative quantity.');
            return;
        }

        if (bulkSegment !== 'all' && !bulkSegmentValue) {
            alert('Please select a target segment value.');
            return;
        }

        setBulkLoading(true);
        try {
            const payload = {
                segment: bulkSegment,
                segmentValue: bulkSegmentValue,
                action: bulkAction,
                quantity: Number(bulkQuantity)
            };
            const response = await axios.post(`${API_BASE_URL}/bulk-edit-stock`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message || 'Bulk stock update applied successfully.');
            document.querySelector('#bulkEditStockModal .btn-close')?.click();
            
            // Reset modal states
            setBulkSegment('all');
            setBulkSegmentValue('');
            setBulkAction('set');
            setBulkQuantity('');

            // Refresh product list
            getProductData(searchTerm, 1, false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setBulkLoading(false);
        }
    };

    // Quick individual stock restock handlers
    const [focusStock, setFocusStock] = useState({});
    const [editingProductId, setEditingProductId] = useState(null);

    const handleQuickStockChange = async (productId, currentStock, delta) => {
        const newStock = Math.max(0, Number(currentStock || 0) + delta);
        
        // Optimistic update
        setProductList(prev => prev.map(p => p._id === productId ? { ...p, stock: newStock } : p));
        
        try {
            await axios.post(`${API_BASE_URL}/bulk-edit-stock`, {
                segment: 'individual',
                segmentValue: productId,
                action: 'set',
                quantity: newStock
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            console.error('Error updating stock:', error);
            // Revert on failure
            setProductList(prev => prev.map(p => p._id === productId ? { ...p, stock: currentStock } : p));
            alert('Failed to update stock. Please try again.');
        }
    };

    const handleQuickStockSet = (productId, value) => {
        const cleanVal = value === '' ? '' : Number(value);
        setProductList(prev => prev.map(p => p._id === productId ? { ...p, stock: cleanVal } : p));
    };

    const handleQuickStockSave = async (productId, finalValue, originalValue) => {
        const stockVal = finalValue === '' ? 0 : Number(finalValue);
        if (isNaN(stockVal) || stockVal < 0) {
            alert('Please enter a valid stock number.');
            setProductList(prev => prev.map(p => p._id === productId ? { ...p, stock: originalValue } : p));
            setEditingProductId(null);
            return;
        }

        // Lock editing mode
        setEditingProductId(null);

        // If nothing changed, skip API call
        if (Number(stockVal) === Number(originalValue)) return;

        try {
            await axios.post(`${API_BASE_URL}/bulk-edit-stock`, {
                segment: 'individual',
                segmentValue: productId,
                action: 'set',
                quantity: stockVal
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            console.error('Error updating stock:', error);
            setProductList(prev => prev.map(p => p._id === productId ? { ...p, stock: originalValue } : p));
            alert('Failed to update stock. Please try again.');
        }
    };

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getProductData = async (searchQuery = '', pageNum = 1, append = false) => {
        if (pageNum === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/product-list?page=${pageNum}&limit=100&search=${searchQuery}&sortBy=${sortBy}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const { products = [], total = 0, pages = 1 } = response.data;
            if (append) {
                setProductList(prev => [...prev, ...products]);
            } else {
                setProductList(products);
            }
            setTotalItems(total);
            setHasMore(pageNum < pages);
            setCurrentPage(pageNum);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const getCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categorydata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getBrands = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/branddata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    // Debounced search (server-side filter)
    const debounceRef = useRef();
    const handleSearchProduct = (event) => {
        const val = event.target.value;
        setSearchTerm(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            getProductData(val, 1, false);
        }, 300);
    };

    const loadMoreProducts = () => {
        if (!hasMore || loadingMore || loading) return;
        getProductData(searchTerm, currentPage + 1, true);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 100) {
                loadMoreProducts();
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [currentPage, hasMore, loadingMore, loading, searchTerm]);

    const handleChange = (event) => {
        setProductData({
            ...productData,
            [event.target.name]: event.target.value
        });
    };

    const handleImageUpload = async (event) => {
        const { name } = event.target;
        const file = event.target.files[0];

        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, or WebP images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        setUploadInProgress((prev) => prev + 1);
        try {
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProductData((prevData) => ({
                ...prevData,
                [name]: response.data.path
            }));
            setImageUploads((prevUploads) => ({
                ...prevUploads,
                [name]: true
            }));
            alert("Image uploaded successfully!");
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadInProgress((prev) => prev - 1);
        }
    };

    const validateFields = () => {
        const requiredFields = ['name', 'brand', 'category', 'originalPrice', 'price', 'quantity', 'stock', 'unit', 'image1'];

        for (let field of requiredFields) {
            if (!productData[field] || productData[field].trim() === '') {
                alert(`${field} is required.`);
                return false;
            }
        }

        if (!imageUploads.image1) {
            alert('First image (Image 1) must be uploaded before saving.');
            return false;
        }

        return true;
    };

    const handleSaveData = async () => {
        if (!validateFields()) return;

        setUploadInProgress((prev) => prev + 1);
        try {
            const response = await axios.post(`${API_BASE_URL}/save-product-data`, productData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);

            setProductData({});
            setImageUploads({ image1: false, image2: false, image3: false, image4: false });
            getProductData();
            // Close modal using data-bs-dismiss attribute
            const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadInProgress((prev) => prev - 1);
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/delete-product/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            getProductData();
        } catch (error) {
            handleApiError(error);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    useEffect(() => {
        getCategories();
        getBrands();
    }, []);

    useEffect(() => {
        getProductData(searchTerm, 1, false);
    }, [sortBy]);

    // Determine if all required fields (without alerts) are filled for enabling Save button
    const requiredForEnable = ['name','brand','category','originalPrice','price','quantity','stock','unit','image1'];
    const canSave = requiredForEnable.every(f => {
        const v = productData[f];
        return v !== undefined && v !== null && String(v).trim() !== '';
    });
    const isSaveDisabled = uploadInProgress > 0 || !canSave || !imageUploads.image1;

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">PRODUCT MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage your product catalog and inventory</p>
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="add-brand-btn" /* reuse brand button styling for consistency */
                                    data-bs-toggle="modal"
                                    data-bs-target="#exampleModal"
                                    aria-label="Add a new product"
                                >
                                    <FaPlus className="icon" />
                                    <span className="txt">Add Product</span>
                                </button>
                                <button
                                    type="button"
                                    className="add-brand-btn"
                                    style={{ backgroundColor: '#0f172a', borderColor: '#0f172a' }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#bulkEditStockModal"
                                    aria-label="Bulk restock products"
                                >
                                    <FaBox className="icon" style={{ marginRight: '6px' }} />
                                    <span className="txt">Restock Products</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pending-control-panel">
                    <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ maxWidth: '700px' }}>
                        <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '420px' }}>
                            <input
                                type="text"
                                className="form-control search-input-custom w-100"
                                placeholder="Search by product name, brand, or category..."
                                value={searchTerm}
                                onChange={handleSearchProduct}
                            />
                            <FaSearch className="search-icon-custom" />
                        </div>

                        <div className="d-flex align-items-center gap-2" style={{ minWidth: '240px' }}>
                            <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Sort By:</small>
                            <select
                                className="form-select sort-select-custom"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="price_asc">Price: Low to High</option>
                                <option value="price_desc">Price: High to Low</option>
                                <option value="name_asc">Name: A to Z</option>
                                <option value="name_desc">Name: Z to A</option>
                                <option value="stock_asc">Stock: Low to High</option>
                                <option value="stock_desc">Stock: High to Low</option>
                            </select>
                        </div>
                    </div>

                    <div className="d-flex align-items-center">
                        <small className="text-muted fw-semibold">
                            Total Products: {totalItems}
                        </small>
                    </div>
                </div>

                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div className="row mt-0 g-0">
                        <div className="col-12">
                            {productList.length > 0 ? (
                                <div className="card border-0 rounded-0 shadow-none order-table-card">
                                    {/* <div className="card-header bg-white border-0">
                                        <h5 className="mb-0">
                                            <FaBox className="text-primary me-2" />
                                            Products ({filteredProducts.length})
                                        </h5>
                                    </div> */}
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="border-0" style={{ width: '50px' }}>#</th>
                                                        <th className="border-0">Product Details</th>
                                                        <th className="border-0" style={{ width: '120px' }}>Price</th>
                                                        <th className="border-0" style={{ width: '150px' }}>Stock</th>
                                                        <th className="border-0" style={{ width: '100px' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {productList.map((product, index) => {
                                                        const stockNum = Number(product.stock ?? 0);
                                                        const lowStock = stockNum > 0 && stockNum < 5;
                                                        const outOfStock = stockNum <= 0;
                                                        const rowNumber = index + 1;
                                                        return (
                                                            <tr key={product._id || rowNumber} className={outOfStock ? 'table-danger' : (lowStock ? 'table-warning' : '')}>
                                                                <td className="align-middle text-center"><span className="badge bg-secondary">{rowNumber}</span></td>
                                                                <td className="align-middle">
                                                                    <div className="d-flex gap-2 align-items-start">
                                                                        {product.image1 && (
                                                                            <img
                                                                                src={
                                                                                    product.image1 && product.image1.startsWith('http')
                                                                                        ? product.image1
                                                                                        : `${API_BASE_URL}${product.image1}`
                                                                                }
                                                                                alt={product.name}
                                                                                style={{width:45,height:45,objectFit:'cover',borderRadius:4}}
                                                                                onError={(e)=>{e.currentTarget.style.opacity='0.4';}}
                                                                            />
                                                                        )}
                                                                        <div>
                                                                            <strong className="text-dark d-block">{product.name}</strong>
                                                                            <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                                                                                <span className="badge bg-info text-dark" style={{ fontSize: '10px' }}>{product.category}</span>
                                                                                <span className="badge bg-warning text-dark" style={{ fontSize: '10px' }}>{product.brand}</span>
                                                                                <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>{product.quantity} {product.unit}</span>
                                                                                {(() => {
                                                                                    const rating = Number(product.rating || 0);
                                                                                    const stars = [];
                                                                                    for (let i = 1; i <= 5; i++) {
                                                                                        if (rating >= i) {
                                                                                            stars.push(<FaStar key={i} className="text-warning" style={{ fontSize: '10px' }} />);
                                                                                        } else if (rating >= i - 0.5) {
                                                                                            stars.push(<FaStarHalfAlt key={i} className="text-warning" style={{ fontSize: '10px' }} />);
                                                                                        } else {
                                                                                           stars.push(<FaRegStar key={i} className="text-warning" style={{ fontSize: '10px' }} />);
                                                                                        }
                                                                                    }
                                                                                    return <span className="d-inline-flex align-items-center gap-0 ms-1">{stars}<small className="text-muted ms-1" style={{ fontSize: '10px' }}>({rating.toFixed(1)})</small></span>;
                                                                                })()}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="align-middle">
                                                                    <strong className="text-success d-block">{formatCurrency(product.price)}</strong>
                                                                    {product.originalPrice && product.originalPrice > product.price && (
                                                                        <span className="text-muted text-decoration-line-through small" style={{ fontSize: '11px' }}>{formatCurrency(product.originalPrice)}</span>
                                                                    )}
                                                                </td>
                                                                 <td className="align-middle text-center">
                                                                     {editingProductId === product._id ? (() => {
                                                                         const stockCount = Number(product.stock ?? 0);
                                                                         const isOut = stockCount <= 0;
                                                                         const isLow = stockCount > 0 && stockCount < 5;
                                                                         
                                                                         let borderColor = '#cbd5e1';
                                                                         let backgroundColor = '#ffffff';
                                                                         let textColor = '#475569';
                                                                         
                                                                         if (isOut) {
                                                                             borderColor = '#fca5a5';
                                                                             backgroundColor = '#fef2f2';
                                                                             textColor = '#b91c1c';
                                                                         } else if (isLow) {
                                                                             borderColor = '#fde047';
                                                                             backgroundColor = '#fefce8';
                                                                             textColor = '#a16207';
                                                                         }
                                                                         
                                                                         return (
                                                                             <div className="d-flex align-items-center justify-content-center">
                                                                                 <div 
                                                                                     className="d-flex align-items-center justify-content-between border" 
                                                                                     style={{ 
                                                                                         width: '95px', 
                                                                                         height: '28px', 
                                                                                         borderRadius: '0px', 
                                                                                         borderColor: borderColor, 
                                                                                         backgroundColor: backgroundColor,
                                                                                         padding: '0px 2px',
                                                                                         boxShadow: '0 1px 2px rgba(0,0,0,0.01)',
                                                                                         transition: 'all 0.2s ease'
                                                                                     }}
                                                                                 >
                                                                                     {/* Minus button */}
                                                                                     <button 
                                                                                         type="button" 
                                                                                         className="btn p-0 d-flex align-items-center justify-content-center border-0" 
                                                                                         style={{ 
                                                                                             width: '24px', 
                                                                                             height: '100%', 
                                                                                             fontSize: '15px', 
                                                                                             fontWeight: '600',
                                                                                             color: textColor,
                                                                                             backgroundColor: 'transparent',
                                                                                             outline: 'none',
                                                                                             boxShadow: 'none',
                                                                                             cursor: isOut ? 'not-allowed' : 'pointer'
                                                                                         }}
                                                                                         onClick={() => handleQuickStockChange(product._id, product.stock, -1)}
                                                                                         disabled={isOut}
                                                                                     >
                                                                                         -
                                                                                     </button>

                                                                                     {/* Input quantity */}
                                                                                     <input 
                                                                                         type="text" 
                                                                                         className="form-control text-center p-0 m-0 border-0 fw-semibold" 
                                                                                         style={{ 
                                                                                             width: '40px', 
                                                                                             height: '100%', 
                                                                                             fontSize: '0.8rem', 
                                                                                             color: isOut ? '#b91c1c' : (isLow ? '#a16207' : '#0f172a'),
                                                                                             boxShadow: 'none',
                                                                                             backgroundColor: 'transparent'
                                                                                         }}
                                                                                         value={product.stock ?? 0}
                                                                                         onChange={(e) => handleQuickStockSet(product._id, e.target.value)}
                                                                                         autoFocus
                                                                                         onFocus={(e) => {
                                                                                             e.target.select();
                                                                                             setFocusStock(prev => ({ ...prev, [product._id]: product.stock }));
                                                                                         }}
                                                                                         onBlur={() => {
                                                                                             const orig = focusStock[product._id] ?? product.stock;
                                                                                             handleQuickStockSave(product._id, product.stock, orig);
                                                                                         }}
                                                                                         onKeyDown={(e) => {
                                                                                             if (e.key === 'Enter') {
                                                                                                 e.target.blur();
                                                                                             }
                                                                                         }}
                                                                                     />

                                                                                     {/* Plus button */}
                                                                                     <button 
                                                                                         type="button" 
                                                                                         className="btn p-0 d-flex align-items-center justify-content-center border-0" 
                                                                                         style={{ 
                                                                                             width: '24px', 
                                                                                             height: '100%', 
                                                                                             fontSize: '13px', 
                                                                                             fontWeight: '600',
                                                                                             color: textColor,
                                                                                             backgroundColor: 'transparent',
                                                                                             outline: 'none',
                                                                                             boxShadow: 'none'
                                                                                         }}
                                                                                         onClick={() => handleQuickStockChange(product._id, product.stock, 1)}
                                                                                     >
                                                                                         +
                                                                                     </button>
                                                                                 </div>
                                                                             </div>
                                                                         );
                                                                     })() : (
                                                                         <div 
                                                                             onClick={() => setEditingProductId(product._id)}
                                                                             style={{ cursor: 'pointer' }}
                                                                             title="Click to edit stock"
                                                                             className="d-inline-flex align-items-center gap-1.5"
                                                                         >
                                                                             <span className={`badge px-2 py-1 ${outOfStock ? 'bg-danger' : (lowStock ? 'bg-warning text-dark' : 'bg-success')}`} style={{ fontSize: '0.8rem' }}>
                                                                                 {outOfStock ? 'Out of Stock' : (lowStock ? `Low stock (${product.stock})` : `${product.stock} in stock`)}
                                                                             </span>
                                                                         </div>
                                                                     )}
                                                                 </td>
                                                                <td className="align-middle">
                                                                    <div className="order-actions-wrapper">
                                                                        <Link to={`/admin/products/edit/${product._id}`} className="order-action-btn order-action-btn-view" title="Edit Product"><FaPen /></Link>
                                                                        <button className="order-action-btn order-action-btn-cancel" onClick={()=>handleDeleteProduct(product._id)} title="Delete Product"><FaTrash /></button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Pagination controls moved below to mimic CustomerPage layout */}
                                    </div>
                                </div>
                            ) : (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaBox className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No products found matching your search.' : 'No products found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first product to get started.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {loadingMore && (
                    <div className="text-center py-4 bg-white" style={{ borderBottom: '1px solid #cbd5e1', borderLeft: '1px solid #cbd5e1', borderRight: '1px solid #cbd5e1', marginLeft: '-1rem', marginRight: '-1rem', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                        <div className="spinner-border spinner-border-sm text-danger" role="status">
                            <span className="visually-hidden">Loading more...</span>
                        </div>
                        <span className="ms-2 text-muted small fw-semibold">Loading more products...</span>
                    </div>
                )}
            </div>

            {/* Add Product Modal (styled similar to Add Brand modal) */}
            <div className="modal fade add-product-modal add-brand-modal" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">Add New Product</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body pt-0">
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Product Name</label>
                                        <input 
                                            type="text" 
                                            className="form-control rounded-0" 
                                            placeholder="Enter product name" 
                                            name="name" 
                                            value={productData.name || ''} 
                                            onChange={handleChange} 
                                        />
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Brand</label>
                                        <select 
                                            name="brand" 
                                            className="form-select rounded-0"
                                            value={productData.brand || ''} 
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Brand</option>
                                            {brands.length > 0 ? brands.map((brand) => (
                                                <option key={brand._id} value={brand.name}>{brand.name}</option>
                                            )) : <option>Loading...</option>}
                                        </select>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Category</label>
                                        <select 
                                            name="category" 
                                            className="form-select rounded-0"
                                            value={productData.category || ''} 
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Category</option>
                                            {categories.length > 0 ? categories.map((category) => (
                                                <option key={category._id} value={category.name}>{category.name}</option>
                                            )) : <option>Loading...</option>}
                                        </select>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Original Price (MRP)</label>
                                        <input
                                            type="number"
                                            className="form-control rounded-0"
                                            placeholder="Enter original price (MRP)"
                                            min={0}
                                            name="originalPrice"
                                            value={productData.originalPrice || ''}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Discounted Price</label>
                                        <input
                                            type="number"
                                            className="form-control rounded-0"
                                            placeholder="Enter discounted price"
                                            min={0}
                                            name="price"
                                            value={productData.price || ''}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Quantity</label>
                                        <input 
                                            type="text" 
                                            className="form-control rounded-0" 
                                            placeholder="Enter quantity" 
                                            name="quantity" 
                                            value={productData.quantity || ''} 
                                            onChange={handleChange} 
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Stock</label>
                                        <input 
                                            type="number" 
                                            className="form-control rounded-0" 
                                            placeholder="Enter stock" 
                                            name="stock" 
                                            min="0"
                                            value={productData.stock || ''} 
                                            onChange={handleChange} 
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Rating</label>
                                        <div className="d-flex align-items-center gap-1">
                                            {[1,2,3,4,5].map(i => (
                                                <span
                                                    key={i}
                                                    style={{cursor:'pointer'}}
                                                    onClick={()=> setProductData({...productData, rating: i})}
                                                    title={`${i} star${i>1?'s':''}`}
                                                >
                                                    { (productData.rating || 0) >= i ? <FaStar className="text-warning" /> : <FaRegStar className="text-warning" /> }
                                                </span>
                                            ))}
                                            <small className="ms-2 text-muted">{productData.rating || 0} / 5</small>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Unit</label>
                                        <div className="input-group">
                                            <input 
                                                type="text" 
                                                className="form-control rounded-0" 
                                                name="unit" 
                                                value={productData.unit || ''} 
                                                onChange={handleChange} 
                                                placeholder="Type or select unit"
                                            />
                                            <button 
                                                className="btn btn-outline-secondary dropdown-toggle rounded-0" 
                                                type="button" 
                                                data-bs-toggle="dropdown" 
                                                aria-expanded="false"
                                            >
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end rounded-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {['cm', 'g', 'gm', 'jar', 'kg', 'l', 'lkg', 'Ltr', 'ml', 'ml.', 'mtr', 'p', 'pack', 'pc', 'pck', 'pcs'].map((u) => (
                                                    <li key={u}>
                                                        <button 
                                                            className="dropdown-item" 
                                                            type="button" 
                                                            onClick={() => setProductData({ ...productData, unit: u })}
                                                        >
                                                            {u}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Description</label>
                                        <textarea 
                                            rows={3} 
                                            className="form-control rounded-0"
                                            name="description" 
                                            value={productData.description || ''} 
                                            onChange={handleChange} 
                                            placeholder="Enter product description"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">Product Images</label>
                                <small className="text-muted d-block mb-2">* First image becomes the cover and is required. Other images are optional. *</small>
                                <div className="row g-2">
                                    {['image1','image2','image3','image4'].map((imgKey, idx) => (
                                        <div className="col-sm-6" key={imgKey}>
                                            <div className="border p-2 bg-light h-100 d-flex flex-column justify-content-between" style={{borderRadius:0}}>
                                                <div>
                                                    <small className="text-muted d-block mb-1">Image {idx+1} {idx === 0 ? <span className="text-danger fw-bold">*</span> : ''}</small>
                                                    <input className="form-control form-control-sm rounded-0" type="file" accept="image/*" name={imgKey} onChange={handleImageUpload} />
                                                </div>
                                                {productData[imgKey] && (
                                                    <div className="mt-2 text-center bg-white border position-relative" style={{minHeight:90,borderRadius:0}}>
                                                        <img
                                                            src={
                                                                productData[imgKey] && productData[imgKey].startsWith('http')
                                                                    ? productData[imgKey]
                                                                    : `${API_BASE_URL}${productData[imgKey]}`
                                                            }
                                                            alt={`Preview ${imgKey}`}
                                                            className="img-fluid"
                                                            style={{maxHeight:85, objectFit:'contain', borderRadius:0}}
                                                            onError={(e)=>{ e.currentTarget.style.opacity='0.3'; }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {uploadInProgress > 0 && (
                                    <div className="mt-2 d-flex align-items-center gap-2">
                                        <FaSpinner className="spinner-border spinner-border-sm" />
                                        <small className="text-muted">Uploading images...</small>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer pt-0">
                            <button type="button" className="btn btn-light rounded-0 border" data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn save-brand-btn rounded-0" /* reuse save-brand-btn style */
                                onClick={handleSaveData}
                                disabled={isSaveDisabled}
                            >
                                {uploadInProgress > 0 ? (
                                    <>
                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Product'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                </div>
                <div className='mt-5 mb-5 pb-5'></div>

                <div className="modal fade add-brand-modal" id="bulkEditStockModal" tabIndex={-1} aria-labelledby="bulkEditStockModalLabel" aria-hidden="true" style={{ zIndex: 3000 }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 3001 }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold">Bulk Edit Stock</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>

                            {/* Modal Body */}
                            <form onSubmit={handleBulkStockSubmit}>
                                <div className="modal-body pt-0">
                                    
                                    {/* 1. Target Products Segment */}
                                    <div className="mb-3 mt-3">
                                        <label className="form-label">1. Target Products Segment</label>
                                        <select 
                                            className="form-select rounded-0"
                                            value={bulkSegment}
                                            onChange={(e) => {
                                                setBulkSegment(e.target.value);
                                                setBulkSegmentValue('');
                                            }}
                                        >
                                            <option value="all">All Products (Full Catalog)</option>
                                            <option value="category">By Category</option>
                                            <option value="brand">By Brand</option>
                                        </select>
                                    </div>

                                    {/* Dependent sub-selectors */}
                                    {bulkSegment === 'category' && (
                                        <div className="mb-3">
                                            <label className="form-label">Select Category</label>
                                            <select 
                                                className="form-select rounded-0"
                                                value={bulkSegmentValue}
                                                onChange={(e) => setBulkSegmentValue(e.target.value)}
                                                required
                                            >
                                                <option value="">-- Choose Category --</option>
                                                {categories.map((cat) => (
                                                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {bulkSegment === 'brand' && (
                                        <div className="mb-3">
                                            <label className="form-label">Select Brand</label>
                                            <select 
                                                className="form-select rounded-0"
                                                value={bulkSegmentValue}
                                                onChange={(e) => setBulkSegmentValue(e.target.value)}
                                                required
                                            >
                                                <option value="">-- Choose Brand --</option>
                                                {brands.map((brand) => (
                                                    <option key={brand._id} value={brand.name}>{brand.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* 2. Stock Action */}
                                    <div className="mb-3">
                                        <label className="form-label">2. Stock Action</label>
                                        <select 
                                            className="form-select rounded-0"
                                            value={bulkAction}
                                            onChange={(e) => setBulkAction(e.target.value)}
                                        >
                                            <option value="set">Set Stock To (Overwrite)</option>
                                            <option value="add">Add to Current Stock</option>
                                            <option value="subtract">Subtract from Current Stock</option>
                                        </select>
                                    </div>

                                    {/* 3. Stock Quantity (Units) */}
                                    <div className="mb-3">
                                        <label className="form-label">3. Stock Quantity (Units)</label>
                                        <input 
                                            type="number"
                                            className="form-control rounded-0"
                                            placeholder="e.g. 100"
                                            value={bulkQuantity}
                                            onChange={(e) => setBulkQuantity(e.target.value)}
                                            min="0"
                                            required
                                        />
                                    </div>

                                </div>

                                {/* Modal Footer */}
                                <div className="modal-footer pt-0">
                                    <button 
                                        type="button" 
                                        className="btn btn-light rounded-0 border"
                                        data-bs-dismiss="modal"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn save-brand-btn rounded-0"
                                        disabled={bulkLoading}
                                    >
                                        {bulkLoading ? (
                                            <>
                                                <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                Applying...
                                            </>
                                        ) : (
                                            'Apply Changes'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

            <style jsx>{`
                .sort-select-custom {
                    height: 38px !important;
                    border: 1px solid #cbd5e1 !important;
                    border-radius: 4px !important;
                    font-size: 0.88rem !important;
                    color: #334155 !important;
                    padding-left: 12px !important;
                    padding-right: 36px !important;
                    background-color: #ffffff !important;
                    cursor: pointer !important;
                    box-shadow: none !important;
                    transition: all 0.2s ease-in-out !important;
                }

                .sort-select-custom:focus {
                    border-color: #ed3237 !important;
                    box-shadow: 0 0 0 2px rgba(237, 50, 55, 0.1) !important;
                    outline: none !important;
                }
            `}</style>
        </Fragment>
    );
}

export default ProductsPage;
